import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { toast } from 'sonner';
import {
  User, Briefcase, DollarSign, FileText, Gift, Package, TrendingUp, History,
  ArrowLeft, Save, Upload, Trash2, Calendar, Building2, Phone, Mail,
  MapPin, Shield, CreditCard, AlertCircle, CheckCircle, Clock, Download
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Utility function
const getInitials = (firstName, lastName) => {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

const formatCurrency = (amount) => {
  if (!amount) return '₱0.00';
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
};

const EmployeeProfile = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [documents, setDocuments] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [promotionHistory, setPromotionHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const isOwner = user?.role === 'owner';
  const canViewSalary = isOwner || user?.role === 'admin';

  useEffect(() => {
    fetchEmployeeProfile();
    fetchDocuments();
    if (canViewSalary) {
      fetchSalaryHistory();
      fetchPromotionHistory();
    }
  }, [employeeId]);

  const fetchEmployeeProfile = async () => {
    try {
      const token = localStorage.getItem('wos_token');
      const response = await axios.get(`${API_URL}/api/hris/employees/${employeeId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployee(response.data);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load employee profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('wos_token');
      const response = await axios.get(`${API_URL}/api/hris/employees/${employeeId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const fetchSalaryHistory = async () => {
    try {
      const token = localStorage.getItem('wos_token');
      const response = await axios.get(`${API_URL}/api/hris/employees/${employeeId}/salary-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalaryHistory(response.data);
    } catch (error) {
      console.error('Failed to load salary history:', error);
    }
  };

  const fetchPromotionHistory = async () => {
    try {
      const token = localStorage.getItem('wos_token');
      const response = await axios.get(`${API_URL}/api/hris/employees/${employeeId}/promotion-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromotionHistory(response.data);
    } catch (error) {
      console.error('Failed to load promotion history:', error);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('wos_token');
      await axios.put(`${API_URL}/api/hris/employees/${employeeId}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      setEditMode(false);
      fetchEmployeeProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      on_leave: 'bg-yellow-100 text-yellow-800',
      resigned: 'bg-gray-100 text-gray-800',
      terminated: 'bg-red-100 text-red-800',
      suspended: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getEmploymentTypeBadge = (type) => {
    const colors = {
      regular: 'bg-blue-100 text-blue-800',
      probationary: 'bg-yellow-100 text-yellow-800',
      contractual: 'bg-purple-100 text-purple-800',
      part_time: 'bg-orange-100 text-orange-800',
      intern: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout title="Employee Profile" subtitle="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!employee) {
    return (
      <DashboardLayout title="Employee Profile" subtitle="Employee not found">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Employee not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title={`${employee.first_name} ${employee.last_name}`}
      subtitle="Employee Profile"
    >
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Profile Header */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={employee.avatar_url} alt={employee.first_name} />
              <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                {getInitials(employee.first_name, employee.last_name)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold">{employee.first_name} {employee.last_name}</h2>
                <Badge className={getStatusBadge(employee.employment_status || 'active')}>
                  {(employee.employment_status || 'active').replace('_', ' ')}
                </Badge>
                {employee.employment_type && (
                  <Badge className={getEmploymentTypeBadge(employee.employment_type)}>
                    {employee.employment_type.replace('_', ' ')}
                  </Badge>
                )}
              </div>
              <p className="text-lg text-muted-foreground mb-2">
                {employee.job_title || 'No job title'} • {employee.department_name || 'No department'}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  {employee.email}
                </span>
                {employee.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {employee.phone}
                  </span>
                )}
                {employee.employee_id && (
                  <span className="flex items-center gap-1">
                    <Shield className="h-4 w-4" />
                    ID: {employee.employee_id}
                  </span>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <Button variant="outline" onClick={() => { setEditMode(false); setFormData(employee); }}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveProfile} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setEditMode(true)}>
                    Edit Profile
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="employment" className="gap-2">
            <Briefcase className="h-4 w-4" />
            Employment
          </TabsTrigger>
          {canViewSalary && (
            <TabsTrigger value="payroll" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Payroll
            </TabsTrigger>
          )}
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <p className="text-sm font-medium">{employee.first_name}</p>
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <p className="text-sm font-medium">{employee.last_name}</p>
                  </div>
                </div>
                
                {editMode ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Birthdate</Label>
                        <Input
                          type="date"
                          value={formData.birthdate || ''}
                          onChange={(e) => handleInputChange('birthdate', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Gender</Label>
                        <Select
                          value={formData.gender || ''}
                          onValueChange={(v) => handleInputChange('gender', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                            <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Civil Status</Label>
                      <Select
                        value={formData.civil_status || ''}
                        onValueChange={(v) => handleInputChange('civil_status', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married">Married</SelectItem>
                          <SelectItem value="widowed">Widowed</SelectItem>
                          <SelectItem value="separated">Separated</SelectItem>
                          <SelectItem value="divorced">Divorced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Textarea
                        value={formData.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Full address"
                      />
                    </div>
                    <div>
                      <Label>Contact Number</Label>
                      <Input
                        value={formData.contact_number || ''}
                        onChange={(e) => handleInputChange('contact_number', e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Birthdate</Label>
                        <p className="text-sm font-medium">{formatDate(employee.birthdate)}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Gender</Label>
                        <p className="text-sm font-medium capitalize">{employee.gender?.replace('_', ' ') || '-'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Civil Status</Label>
                      <p className="text-sm font-medium capitalize">{employee.civil_status || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Address</Label>
                      <p className="text-sm font-medium">{employee.address || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Contact Number</Label>
                      <p className="text-sm font-medium">{employee.contact_number || employee.phone || '-'}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Emergency Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <>
                    <div>
                      <Label>Contact Name</Label>
                      <Input
                        value={formData.emergency_contact_name || ''}
                        onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                        placeholder="Emergency contact name"
                      />
                    </div>
                    <div>
                      <Label>Contact Number</Label>
                      <Input
                        value={formData.emergency_contact_number || ''}
                        onChange={(e) => handleInputChange('emergency_contact_number', e.target.value)}
                        placeholder="Emergency contact number"
                      />
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Input
                        value={formData.emergency_contact_relationship || ''}
                        onChange={(e) => handleInputChange('emergency_contact_relationship', e.target.value)}
                        placeholder="e.g., Spouse, Parent, Sibling"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-muted-foreground">Contact Name</Label>
                      <p className="text-sm font-medium">{employee.emergency_contact_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Contact Number</Label>
                      <p className="text-sm font-medium">{employee.emergency_contact_number || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Relationship</Label>
                      <p className="text-sm font-medium">{employee.emergency_contact_relationship || '-'}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Government IDs */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Government Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-muted-foreground">SSS Number</Label>
                    <p className="text-sm font-medium">{employee.sss_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">PhilHealth Number</Label>
                    <p className="text-sm font-medium">{employee.philhealth_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Pag-IBIG Number</Label>
                    <p className="text-sm font-medium">{employee.pagibig_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">TIN</Label>
                    <p className="text-sm font-medium">{employee.tin_number || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employment Tab */}
        <TabsContent value="employment">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Employment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <>
                    <div>
                      <Label>Job Title</Label>
                      <Input
                        value={formData.job_title || ''}
                        onChange={(e) => handleInputChange('job_title', e.target.value)}
                        placeholder="Job title"
                      />
                    </div>
                    <div>
                      <Label>Employment Type</Label>
                      <Select
                        value={formData.employment_type || ''}
                        onValueChange={(v) => handleInputChange('employment_type', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="regular">Regular</SelectItem>
                          <SelectItem value="probationary">Probationary</SelectItem>
                          <SelectItem value="contractual">Contractual</SelectItem>
                          <SelectItem value="part_time">Part Time</SelectItem>
                          <SelectItem value="intern">Intern</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Employment Status</Label>
                      <Select
                        value={formData.employment_status || ''}
                        onValueChange={(v) => handleInputChange('employment_status', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="on_leave">On Leave</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="resigned">Resigned</SelectItem>
                          <SelectItem value="terminated">Terminated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Hire Date</Label>
                      <Input
                        type="date"
                        value={formData.hire_date || ''}
                        onChange={(e) => handleInputChange('hire_date', e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-muted-foreground">Job Title</Label>
                      <p className="text-sm font-medium">{employee.job_title || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Department</Label>
                      <p className="text-sm font-medium">{employee.department_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Manager</Label>
                      <p className="text-sm font-medium">{employee.manager_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Shift</Label>
                      <p className="text-sm font-medium">{employee.shift_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Hire Date</Label>
                      <p className="text-sm font-medium">{formatDate(employee.hire_date)}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Important Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <>
                    <div>
                      <Label>Probation End Date</Label>
                      <Input
                        type="date"
                        value={formData.probation_end_date || ''}
                        onChange={(e) => handleInputChange('probation_end_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Regularization Date</Label>
                      <Input
                        type="date"
                        value={formData.regularization_date || ''}
                        onChange={(e) => handleInputChange('regularization_date', e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-muted-foreground">Hire Date</Label>
                      <p className="text-sm font-medium">{formatDate(employee.hire_date)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Probation End Date</Label>
                      <p className="text-sm font-medium">{formatDate(employee.probation_end_date)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Regularization Date</Label>
                      <p className="text-sm font-medium">{formatDate(employee.regularization_date)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Account Created</Label>
                      <p className="text-sm font-medium">{formatDate(employee.created_at)}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payroll Tab */}
        {canViewSalary && (
          <TabsContent value="payroll">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Compensation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Monthly Salary</Label>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(employee.monthly_salary || employee.salary)}
                    </p>
                  </div>
                  {employee.hourly_rate && (
                    <div>
                      <Label className="text-muted-foreground">Hourly Rate</Label>
                      <p className="text-sm font-medium">{formatCurrency(employee.hourly_rate)}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Bank Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editMode ? (
                    <>
                      <div>
                        <Label>Bank Name</Label>
                        <Input
                          value={formData.bank_name || ''}
                          onChange={(e) => handleInputChange('bank_name', e.target.value)}
                          placeholder="Bank name"
                        />
                      </div>
                      <div>
                        <Label>Account Number</Label>
                        <Input
                          value={formData.bank_account_number || ''}
                          onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                          placeholder="Account number"
                        />
                      </div>
                      <div>
                        <Label>Branch</Label>
                        <Input
                          value={formData.bank_branch || ''}
                          onChange={(e) => handleInputChange('bank_branch', e.target.value)}
                          placeholder="Branch"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label className="text-muted-foreground">Bank Name</Label>
                        <p className="text-sm font-medium">{employee.bank_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Account Number</Label>
                        <p className="text-sm font-medium">{employee.bank_account_number || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Branch</Label>
                        <p className="text-sm font-medium">{employee.bank_branch || '-'}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents ({documents.length})
              </CardTitle>
              {isAdmin && (
                <Link to={`/app/hris/employees/${employeeId}/documents/upload`}>
                  <Button size="sm" className="gap-2">
                    <Upload className="h-4 w-4" />
                    Upload Document
                  </Button>
                </Link>
              )}
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <p className="font-medium">{doc.document_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {doc.document_type.replace('_', ' ')} • v{doc.version}
                            {doc.expiry_date && ` • Expires: ${formatDate(doc.expiry_date)}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={`${API_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-1">
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Salary History */}
            {canViewSalary && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Salary History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {salaryHistory.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No salary changes recorded</p>
                  ) : (
                    <div className="space-y-4">
                      {salaryHistory.map((record) => (
                        <div key={record.id} className="border-l-2 border-primary pl-4">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">
                              {formatCurrency(record.previous_salary)} → {formatCurrency(record.new_salary)}
                            </p>
                            <Badge variant={record.change_type === 'increase' ? 'default' : 'secondary'}>
                              {record.change_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Effective: {formatDate(record.effective_date)}
                          </p>
                          {record.change_reason && (
                            <p className="text-sm mt-1">{record.change_reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Promotion History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Position History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {promotionHistory.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No position changes recorded</p>
                ) : (
                  <div className="space-y-4">
                    {promotionHistory.map((record) => (
                      <div key={record.id} className="border-l-2 border-green-500 pl-4">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{record.new_job_title}</p>
                          <Badge variant="outline">{record.promotion_type}</Badge>
                        </div>
                        {record.previous_job_title && (
                          <p className="text-sm text-muted-foreground">
                            From: {record.previous_job_title}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Effective: {formatDate(record.effective_date)}
                        </p>
                        {record.reason && (
                          <p className="text-sm mt-1">{record.reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default EmployeeProfile;
